import { Stack } from '@mui/material';
import { get, isArray, set } from 'lodash';
import React, { FC, ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBackdrop from '../../../../components/ui-component/loading/LoadingBackdrop';
import MuiSnackbar from '../../../../components/ui-component/mui-snackbar/MuiSnackbar';
import MUIToolTip from '../../../../components/ui-component/mui-tooltip/MUIToolTip';
import { useAuthentication } from '../../../../context/AuthContext';
import { useFetchOrganisationDetails } from '../../../../hooks/FetchOrganisation';
import GetRecords from '../../../../lib/api/GetRecords';
import { PostRecord } from '../../../../lib/api/PostRecord';
import { APIS } from '../../../../lib/constants/apis';
import { ANTRAG_VERBUNDPARTNER } from '../../../../lib/constants/componentsPath';
import {
	ANTRAG_BUTTON_NAME,
	ANTRAG_TYP,
	ANTRAGS_FORMULAREN,
	ANTRAGS_STATUS,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	NACHWEIS_POSITION_STATUS,
	NOCH_NICHT_MOEGLICH_TEXT,
	SKIZZE_UND_ANTRAG_EXTERN,
} from '../../../../lib/constants/constant';
import { PAGES_URLS } from '../../../../lib/constants/routes';
import { enumT, getFieldNamefromDataPath, removeDataKeys, removeNullDeep } from '../../../../utils/helper';
import { AntragButton, AntragsButtonWrapper } from '../FoerderungUebersicht';

// Import schema
import {
	getMittelabrufBankdatenFromLastMittelabrufOrAntrag,
	getMittelabrufeFromProjektJahr,
	getMittelabrufErklaerungenUndAnlagenData,
} from 'src/utils/dashboardHelper';
import { getEinsatzbereichByApi } from 'src/utils/einsatzbereichHelper';
import Schema from '../../../../lib/paths/antrag_FULL_Paths.json';
import FoerderprogrammSchema from '../../../../lib/paths/foerderprogramm_FULL_Paths.json';
import KostenkategorieSchema from '../../../../lib/paths/kostenkategorie_FULL_Paths.json';
import MittelabrufSchema from '../../../../lib/paths/mittelabruf_FULL_Paths.json';
import NachweisPositionSchema from '../../../../lib/paths/nachweisposition_FULL_Paths.json';
import VerwendungsnachweisSchema from '../../../../lib/paths/verwendungsnachweis_FULL_Paths.json';
import { getGeneralAppData } from '../../../public/programms/Details/utils/GetGeneralAppData';
const P = Schema.attributes;
const MA_P = MittelabrufSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;
const VN_P = VerwendungsnachweisSchema.attributes;
// const ZB_P = ZwischenBerichtSchema.attributes;
// const KP_P = KostenPositionSchema.attributes;
const FP_P = FoerderprogrammSchema.attributes;
const KK_P = KostenkategorieSchema.attributes;

const StartupStipendiumFoerderDashboard: FC<{ application: any; foerderProgramm: any }> = ({ application, foerderProgramm }) => {
	const { authenticatedData } = useAuthentication();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [wsError, setWsError] = useState(false);

	const { data: orgaDetails } = useFetchOrganisationDetails(get(authenticatedData, 'organisationId'));

	const currentYear = new Date().getFullYear();
	const antrag = get(application, P.Allgemein_Typ.path) === ANTRAG_TYP.Skizze ? get(application, P.Skizze_Antrags.path, application) : application;
	const projektDauer = get(antrag, P.Allgemein_Projektdauerin_Jahren.path, 0);
	const projektJahrStart = get(antrag, P.Allgemein_Projektstart_Jahr.path);

	const mittelAbruf = get(antrag, P.Foerderantrag_Mittelabrufs.path, []);
	const verwendungsnachweis = get(antrag, P.Foerderantrag_Verwendungsnachweiss.path, []);
	// const zwischenberichte = get(antrag, P.Foerderantrag_Zwischenberichts.path, []);
	const abschlussbericht = get(antrag, P.Foerderantrag_Abschlussberichts.path, []);

	const foerderprogramm = get(antrag, P.Foerderprogramm.path, {});

	const handleCreateFoerderAntrag = async () => {
		const appData = getGeneralAppData(
			foerderProgramm,
			get(foerderProgramm, 'id'),
			ANTRAGS_FORMULAREN.StartUpStipendium,
			ANTRAG_TYP.Antrag,
			orgaDetails,
			get(authenticatedData, 'organisationId'),
		);

		try {
			setLoading(true);
			let res = await PostRecord(APIS.Antrag, { data: { ...appData, Skizze: get(application, 'id') } });
			if (res) {
				navigate(`${PAGES_URLS.OnlineAntrag}`, {
					state: {
						id: get(res, 'data.id'),
						api: APIS.Antrag,
						antragsformular: get(res, `data.${P.Allgemein_Antragsformular.path}`),
					},
				});
			}
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Antrag ', err);
		} finally {
			setLoading(false);
		}
	};
	const handleCreateMittelabruf = async (projektJahr: number) => {
		// autofold/fold_next
		// const mittelabrufeImProjektJahr = mittelAbruf.filter(
		// 	(eachMA: any) =>
		// 		Number(get(eachMA, MA_P.Allgemein_Haushaltsjahr.path, 0)) === projektJahr &&
		// 		get(eachMA, MA_P.Mittelabrufstatus_Mittelabrufstatus.path, '') !== ANTRAGS_STATUS.abgelehnt,
		// );
		const mittelabrufeImProjektJahr = getMittelabrufeFromProjektJahr(mittelAbruf, projektJahr);
		const summeVorangegangenerMittelabrufeImHaushaltsjahr = mittelabrufeImProjektJahr.reduce(
			(acc: number, curr: any) => acc + (Number(get(curr, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bereits_Ausgezahlte_Mittel.path, 0)) || 0),
			0,
		);
		const lastMittelabrufImProjektJahr = mittelabrufeImProjektJahr[mittelabrufeImProjektJahr.length - 1];
		const isFirstMittelabrufImProjektJahr = mittelabrufeImProjektJahr.length === 0;
		const nachweispositionenVomLetztenMittelabruf = get(lastMittelabrufImProjektJahr, MA_P.Mittelabruf_Nachweispositions.path, []);
		const erklaerungenUndAnlagenData = getMittelabrufErklaerungenUndAnlagenData(foerderprogramm);
		console.log('lastMittelabrufImProjektJahr', lastMittelabrufImProjektJahr);
		const MA_Data = {
			[getFieldNamefromDataPath(MA_P.Foerderantrag.path)]: get(antrag, 'id'),
			[getFieldNamefromDataPath(MA_P.Mittelabrufstatus_Mittelabrufstatus.path)]: ANTRAGS_STATUS.inBearbeitung,
			[getFieldNamefromDataPath(MA_P.Allgemein_Haushaltsjahr.path)]: projektJahr,
			[getFieldNamefromDataPath(MA_P.Allgemein_Abrufdatum.path)]: new Date(),
			// Bankdaten
			...getMittelabrufBankdatenFromLastMittelabrufOrAntrag(application),
			[getFieldNamefromDataPath(MA_P.Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung.path)]: get(
				antrag,
				P.Bewilligung_Jahresaufteilung.path,
				[],
			).find((ele: any) => get(ele, 'Projektjahr', '') === projektJahr)?.Zuwendung,
			[getFieldNamefromDataPath(MA_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path)]:
				summeVorangegangenerMittelabrufeImHaushaltsjahr,
			[getFieldNamefromDataPath(MA_P.Kalkulation_Block_1.path)]: [
				{
					Zelle_1: get(antrag, P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path, ''),
					Zelle_2: get(antrag, P.Antragstellende_Organisation_Foerderquote.path, []).find((ele: any) => get(ele, 'Projektjahr') === projektJahr)
						?.Foerderquote_403741,
				},
				...get(antrag, P.Verbundpartner.path, [])
					.filter((ele: any) => get(ele, ANTRAG_VERBUNDPARTNER.Name, '') !== '')
					.map((eachVP: any) => {
						return {
							Zelle_1: get(eachVP, ANTRAG_VERBUNDPARTNER.Name, ''),
							Zelle_2: get(eachVP, ANTRAG_VERBUNDPARTNER.FoerderQuoteBlock, []).find((ele: any) => get(ele, 'Projektjahr') === projektJahr)
								?.Foerderquote_403741,
						};
					}),
			],
			// [getFieldNamefromDataPath(MA_P.Stipendium_Angefallene_Ausgaben.path)]: stipendiumAngefallendeAusgabenVomLetztenMittelabruf,
			// [getFieldNamefromDataPath(MA_P.Stipendium_Anfallende_Ausgaben.path)]: stipendiumAnfallendeAusgabenVomLetztenMittelabruf,
			// [getFieldNamefromDataPath(MA_P.Stipendium_Gesamt.path)]: gesamt,
			// [getFieldNamefromDataPath(MA_P.Stipendium_Abzuegl_Private_Eigenmittel.path)]: stipendiumEigenmittelVomLetztenMittelabruf,
			// [getFieldNamefromDataPath(MA_P.Stipendium_Abzuegl_Oeffentliche_Mittel.path)]: stipendiumOeffentlicheMittelVomLetztenMittelabruf,
			// [getFieldNamefromDataPath(MA_P.Stipendium_Bemessungsgrundlage.path)]: bemessungsgrundlage,
			...erklaerungenUndAnlagenData,
		};
		try {
			setLoading(true);
			let res = await PostRecord(APIS.Mittelabruf, { data: MA_Data });
			if (res) {
				try {
					const kostenkategorieFilter = {
						Allgemein_Antragsformular: { $contains: get(antrag, P.Allgemein_Antragsformular.path, '') },
						Allgemein_Einsatzbereich: { $eq: getEinsatzbereichByApi(APIS.Mittelabruf) },
						Allgemein_Aktiv: { $eq: true },
						Allgemein_Kostenposition: { $eq: KOSTEN_POSITION.Einnahme },
						Allgemein_Kostenart_Finanzierung: { $in: [KOSTEN_ARTEN.Eigenmittel, KOSTEN_ARTEN.OeffentlicheMittel] },
					};
					const kostenkategoriesRes = await GetRecords(APIS.Kostenkategorie, [], kostenkategorieFilter);
					const kostenKategorien = removeNullDeep(removeDataKeys(get(kostenkategoriesRes, 'data', [])));

					const ausgabenNachweisKombinationen = [
						{ status: NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben, kostenart: KOSTEN_ARTEN.Projektausgaben },
						{ status: NACHWEIS_POSITION_STATUS.geplant, kostenart: KOSTEN_ARTEN.Projektausgaben },
					];

					for (const { status, kostenart } of ausgabenNachweisKombinationen) {
						const nachweispositionVomLetztenMittelabruf = (nachweispositionenVomLetztenMittelabruf as any[]).find(
							(eachNachweis: any) => get(eachNachweis, NP_P.Allgemein_Status.path) === status,
						);
						const kopierteAusgaben = Number(get(nachweispositionVomLetztenMittelabruf, NP_P.Allgemein_Ausgaben_Kosten.path));

						await PostRecord(APIS.Nachweisposition, {
							data: {
								Mittelabruf: get(res, 'data.id'),
								Allgemein_Status: status,
								Allgemein_Kostentraeger: KOSTENTRAEGER.Antragsteller,
								Allgemein_Unternehmen_Institution: get(authenticatedData, 'organisationId'),
								Allgemein_Kostenart: KOSTEN_POSITION.Ausgabe,
								Allgemein_Kostenart_Finanzierung: kostenart,
								Allgemein_Bezeichnung: '',
								Allgemein_Ausgaben_Kosten: isFirstMittelabrufImProjektJahr ? null : kopierteAusgaben,
							},
						});
					}
					for (const kostenKategorie of kostenKategorien) {
						const kostenartFinanzierung = get(kostenKategorie, KK_P.Allgemein_Kostenart_Finanzierung.path);
						const nachweispositionVomLetztenMittelabruf = (nachweispositionenVomLetztenMittelabruf as any[]).find(
							(eachNachweis: any) =>
								get(eachNachweis, NP_P.Allgemein_Kostenart_Finanzierung.path) === kostenartFinanzierung &&
								get(eachNachweis, `${NP_P.Allgemein_Kostenkategorie.path}.id`) === get(kostenKategorie, 'id'),
						);
						const kopierteAusgaben = Number(get(nachweispositionVomLetztenMittelabruf, NP_P.Allgemein_Ausgaben_Kosten.path));

						await PostRecord(APIS.Nachweisposition, {
							data: {
								Mittelabruf: get(res, 'data.id'),
								Allgemein_Kostentraeger: KOSTENTRAEGER.Antragsteller,
								Allgemein_Unternehmen_Institution: get(authenticatedData, 'organisationId'),
								Allgemein_Kostenart: KOSTEN_POSITION.Einnahme,
								Allgemein_Kostenart_Finanzierung: kostenartFinanzierung,
								Allgemein_Bezeichnung: '',
								Allgemein_Kostenkategorie: get(kostenKategorie, 'id'),
								Allgemein_Ausgaben_Kosten: isFirstMittelabrufImProjektJahr ? null : kopierteAusgaben,
							},
						});
					}
				} catch (err: any) {
					setWsError(true);
					console.error('ERROR: Creating Nachweispositionen', err);
				} finally {
					const mittelabrufIndex =
						mittelAbruf.filter((eachMA: any) => get(eachMA, MA_P.Allgemein_Haushaltsjahr.path) === projektJahr).length + 1;

					navigate(PAGES_URLS.OnlineAntrag, {
						state: {
							id: get(res, 'data.id'),
							api: APIS.Mittelabruf,
							mittelabrufIndex,
						},
					});
				}
			}
			// **********************************************************************
			//Bitte Beachten: VN wird gerade nicht genutzt, erst am Ende des Jahres. Erstmal auskommentiert, weil Mittelabruf genau wie EPZ umgesetzt werden soll und da auch noch kein VN umgesetzt ist (HA-2649)
			// **********************************************************************
			// if (res) {
			// 	const allKostenPostion = get(antrag, P.Foerderantrag_Kostenpositions.path);
			// 	const allVerbundPartner = get(antrag, P.Verbundpartner.path, []);
			// const vnData = {
			// 	Allgemein_Haushaltsjahr: projektJahr,
			// 	Verwendungsnachweisstatus_Status: ANTRAGS_STATUS.inBearbeitung,
			// 	Foerderantrag: get(antrag, 'id'),
			// 	Mittelabruf: get(res, 'data.id'),
			// 	Allgemein_Foerderquote_Antragstellerin: get(antrag, P.Orga_Foerderquote_Gesamt.path),
			// 	Allgemein_Foerderquote_Projektpartner: [
			// 		...allVerbundPartner.map((eachVPartner: any) => {
			// 			return {
			// 				Partner: get(eachVPartner, 'Verbundpartner.id'),
			// 				Partner_Quote: get(eachVPartner, 'Foerderquote_Gesamt_403741'),
			// 			};
			// 		}),
			// 	],
			// };
			// try {
			// 	let vnRes = await PostRecord(APIS.Verwendungsnachweis, { data: vnData });
			// 	if (vnRes) {
			// 		for (let i = 0; i < allKostenPostion.length; i++) {
			// 			const eachKP = allKostenPostion[i];
			// 			const nachweisData = {
			// 				Allgemein_Nr: i + 1,
			// 				Allgemein_Kostentraeger: get(eachKP, KP_P.Allgemein_Kostentraeger.path),
			// 				Allgemein_Unternehmen_Institution: get(eachKP, `${KP_P.Unternehmen.path}.id`),
			// 				Verwendungsnachweis: get(vnRes, 'data.id'),
			// 				Allgemein_Kostenposition: get(eachKP, 'id'),
			// 				Allgemein_Kostenart: get(eachKP, KP_P.Allgemein_Kostenposition.path),
			// 				Allgemein_Bezeichnung: get(eachKP, KP_P.Allgemein_Bezeichnung.path),
			// 				Kostennachweise_Nachweise: [{ Kommentar: '' }],
			// 				Kalkulation_Personalausgaben_Elektromobilitaet: {
			// 					Name_Der_Beschaeftigten: get(eachKP, KP_P.Allgemein_Bezeichnung.path),
			// 				},
			// 				Stundennachweis_Geleistete_Stunden: getMonatListe(eachKP),
			// 			};
			// 			await PostRecord(APIS.Nachweisposition, { data: nachweisData });
			// 		}
			// 	}
			// } catch (err: any) {
			// 	setWsError(true);
			// 	console.error('ERROR: Creating Verwendungsnachweis');
			// } finally {
			// 	navigate(`${PAGES_URLS.OnlineAntrag}`, { state: { id: get(res, 'data.id'), api: APIS.Mittelabruf } });
			// }
			// }
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Mittelabruf ', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateAbschlussbericht = async () => {
		let ABData = {
			Foerderantrag: get(antrag, 'id'),
			Abschlussberichtstatus_Abschlussberichtsstatus: ANTRAGS_STATUS.inBearbeitung,
			Antragsteller: get(antrag, `${P.Antragstellende_Organisation.path}.id`),
		};
		if (get(antrag, P.Allgemein_Antragsformular.path) === ANTRAGS_FORMULAREN.EmobFundE) {
			set(ABData, `Kurzfassung_Zum_Projektende.0.Kostentraeger`, 'Antragsteller');
			set(ABData, `Kurzfassung_Zum_Projektende.0.Antragsteller`, get(antrag, `${P.Antragstellende_Organisation.path}.id`));

			set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.0.Kostentraeger`, 'Antragsteller');
			set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.0.Antragsteller`, get(antrag, `${P.Antragstellende_Organisation.path}.id`));

			const allVpartnerAntrag = get(antrag, P.Verbundpartner.path, []);
			for (let [index, eachVPartner] of allVpartnerAntrag.entries()) {
				set(ABData, `Kurzfassung_Zum_Projektende.${index + 1}.Kostentraeger`, 'Verbundpartner');
				set(ABData, `Kurzfassung_Zum_Projektende.${index + 1}.Verbundpartner`, get(eachVPartner, 'Verbundpartner.id'));
				set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.${index + 1}.Kostentraeger`, 'Verbundpartner');
				set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.${index + 1}.Verbundpartner`, get(eachVPartner, 'Verbundpartner.id'));
			}
		}
		try {
			setLoading(true);
			let res = await PostRecord(APIS.Abschlussbericht, { data: ABData });
			if (res) navigate(`${PAGES_URLS.OnlineAntrag}`, { state: { id: get(res, 'data.id'), api: APIS.Abschlussbericht } });
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Abschlussbericht ', err);
		} finally {
			setLoading(false);
		}
	};

	const RenderMittelAbrufButtons: FC<{}> = () => {
		const buttons: ReactElement[] = [];
		const hasVN = verwendungsnachweis.length > 0;
		const isInFoerderung = get(antrag, P.Antragstatus_Antragstatus.path) === ANTRAGS_STATUS.inFoerderung;

		// Group Mittelabruf by Haushaltsjahr (project year)
		const groupedMittelabrufNachProjektJahr = mittelAbruf.reduce((acc: any, item: any) => {
			const year = item.attributes.Allgemein_Haushaltsjahr;
			if (!acc[year]) {
				acc[year] = [];
			}
			acc[year].push(item);
			return acc;
		}, {});

		// Loop through each year of the project duration
		for (let i = 0; i < projektDauer; i++) {
			const currentProjektJahr = projektJahrStart + i;
			const mittelabrufForYear = groupedMittelabrufNachProjektJahr[currentProjektJahr];
			const isCurrentYear = currentProjektJahr === currentYear;
			const zuwendungImProjektJahr = Number(
				get(antrag, P.Bewilligung_Jahresaufteilung.path, []).find((ele: any) => get(ele, 'Projektjahr', '') === currentProjektJahr)?.Zuwendung ??
					0,
			);
			const summeVorangegangenerMittelabrufeImHaushaltsjahr = (mittelabrufForYear || []).reduce(
				(acc: number, curr: any) => acc + (Number(get(curr, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bereits_Ausgezahlte_Mittel.path, 0)) || 0),
				0,
			);
			const shouldShowCreateMittelabrufButton = zuwendungImProjektJahr - summeVorangegangenerMittelabrufeImHaushaltsjahr > 0;

			// Determine disabled reason for "Mittelabruf erstellen" button
			let maErstellenDisabledReason: string | null = null;

			if (hasVN) {
				maErstellenDisabledReason = 'Ein Verwendungsnachweis wurde bereits erstellt. Es können keine weiteren Mittelabrufe erstellt werden.';
			} else if (!isCurrentYear) {
				maErstellenDisabledReason = 'Mittelabruf kann nur für das aktuelle Kalenderjahr erstellt werden.';
			} else if (!isInFoerderung) {
				maErstellenDisabledReason = 'Der Antrag befindet sich nicht in Förderung.';
			} else {
				// Check all previous project years: last MA must be freigegeben or abgelehnt
				for (let j = 0; j < i; j++) {
					const prevYear = projektJahrStart + j;
					const prevMAs = groupedMittelabrufNachProjektJahr[prevYear];
					if (prevMAs && prevMAs.length > 0) {
						const prevLastStatus = get(prevMAs[prevMAs.length - 1], MA_P.Mittelabrufstatus_Mittelabrufstatus.path, '');
						if (prevLastStatus !== ANTRAGS_STATUS.freigegeben && prevLastStatus !== ANTRAGS_STATUS.abgelehnt) {
							maErstellenDisabledReason = `Der Mittelabruf für ${prevYear} muss zuerst freigegeben werden.`;
							break;
						}
					}
				}
				// Check current year: if MAs exist, last must be freigegeben or abgelehnt
				if (!maErstellenDisabledReason && mittelabrufForYear && mittelabrufForYear.length > 0) {
					const lastStatus = get(mittelabrufForYear[mittelabrufForYear.length - 1], MA_P.Mittelabrufstatus_Mittelabrufstatus.path, '');
					if (lastStatus !== ANTRAGS_STATUS.freigegeben && lastStatus !== ANTRAGS_STATUS.abgelehnt) {
						maErstellenDisabledReason = 'Sie können einen neuen Mittelabruf erstellen, sobald der vorherige Mittelabruf freigegeben ist.';
					}
				}
			}

			const isMAErstellenDisabled = maErstellenDisabledReason !== null;

			if (mittelabrufForYear && mittelabrufForYear.length > 0) {
				buttons.push(
					<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf für ' + currentProjektJahr}>
						{mittelabrufForYear.map((eachMA: any, index: number) => {
							return (
								<AntragButton
									key={`mittelabruf-${currentProjektJahr}-${index}`}
									name={`Projektjahr: ${get(eachMA, MA_P.Allgemein_Haushaltsjahr.path)} #${index + 1}`}
									description={enumT(get(eachMA, MA_P.Mittelabrufstatus_Mittelabrufstatus.path))}
									onClick={() =>
										navigate(PAGES_URLS.OnlineAntrag, {
											state: {
												id: get(eachMA, 'id'),
												api: APIS.Mittelabruf,
												mittelabrufIndex: index + 1,
											},
										})
									}
								/>
							);
						})}

						{shouldShowCreateMittelabrufButton && (
							<MUIToolTip title={maErstellenDisabledReason ?? ''} showTootip={isMAErstellenDisabled}>
								<AntragButton
									name='Mittelabruf erstellen'
									description=''
									onClick={() => handleCreateMittelabruf(currentProjektJahr)}
									variant={'contained'}
									disabled={isMAErstellenDisabled}
								/>
							</MUIToolTip>
						)}
					</AntragsButtonWrapper>,
				);
			} else {
				if (isInFoerderung && shouldShowCreateMittelabrufButton) {
					buttons.push(
						<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf für ' + currentProjektJahr}>
							<MUIToolTip title={maErstellenDisabledReason ?? ''} showTootip={isMAErstellenDisabled}>
								<AntragButton
									name='Mittelabruf erstellen'
									description=''
									onClick={() => handleCreateMittelabruf(currentProjektJahr)}
									variant={'contained'}
									disabled={isMAErstellenDisabled}
								/>
							</MUIToolTip>
						</AntragsButtonWrapper>,
					);
				} else {
					buttons.push(
						<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf für ' + currentProjektJahr}>
							<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} disabled={true} />
						</AntragsButtonWrapper>,
					);
				}
			}
		}

		return <>{buttons}</>;
	};
	const RenderVerwendungsnachweisButtons: FC<{}> = () => {
		// Nur ein einziger VN pro Förderantrag (kein Jahres-Loop)
		const existingVN = verwendungsnachweis.length > 0 ? verwendungsnachweis[0] : null;

		if (existingVN) {
			return (
				<AntragsButtonWrapper title='Verwendungsnachweis:'>
					<AntragButton
						name='Verwendungsnachweis'
						description={enumT(get(existingVN, VN_P.Verwendungsnachweisstatus_Status.path))}
						onClick={() =>
							navigate(PAGES_URLS.OnlineAntrag, {
								state: {
									id: get(existingVN, 'id'),
									api: APIS.Verwendungsnachweis,
								},
							})
						}
					/>
				</AntragsButtonWrapper>
			);
		}

		// Temporäre client-seitige Änderung: Der Verwendungsnachweis-Button soll immer deaktiviert (grau) sein,
		// da die Verwendungsnachweis-Inhalte für das Startup-Stipendium noch nicht zur Verfügung stehen.
		// Ursprüngliche Logik (auskommentiert):
		// const isInFoerderung = get(antrag, P.Antragstatus_Antragstatus.path) === ANTRAGS_STATUS.inFoerderung;
		// const hasMA = mittelAbruf.length > 0;
		// const lastMAStatus = hasMA ? get(mittelAbruf[mittelAbruf.length - 1], MA_P.Mittelabrufstatus_Mittelabrufstatus.path, '') : '';
		// const isLastMAFreigegeben = lastMAStatus === ANTRAGS_STATUS.freigegeben;
		// const canCreateVN = isInFoerderung && hasMA && isLastMAFreigegeben;
		// let vnDisabledTooltip = '';
		// if (!isInFoerderung) {
		// 	vnDisabledTooltip = 'Der Antrag befindet sich nicht in Förderung.';
		// } else if (!hasMA) {
		// 	vnDisabledTooltip = 'Es muss zuerst mindestens ein Mittelabruf erstellt werden.';
		// } else if (!isLastMAFreigegeben) {
		// 	vnDisabledTooltip = 'Der letzte Mittelabruf muss zuerst freigegeben werden.';
		// }

		const canCreateVN = false;

		const vnDisabledTooltip = 'Der Verwendungsnachweis steht derzeit noch nicht zur Verfügung.';

		return (
			<AntragsButtonWrapper title='Verwendungsnachweis:'>
				<MUIToolTip title={vnDisabledTooltip} showTootip={!canCreateVN}>
					<AntragButton name='Verwendungsnachweis erstellen' description='' onClick={() => {}} variant={'contained'} disabled={!canCreateVN} />
				</MUIToolTip>
			</AntragsButtonWrapper>
		);
	};

	const RenderAbschlussberichtButton: FC<{}> = () => {
		if (isArray(abschlussbericht) && abschlussbericht.length > 0) {
			return (
				<AntragsButtonWrapper title='Sachbericht:'>
					{abschlussbericht.map((eachAbschlussbericht: any, i: number) => {
						return (
							<AntragButton
								key={i}
								name='Sachbericht'
								description='Ansehen'
								onClick={() =>
									navigate(`${PAGES_URLS.OnlineAntrag}`, {
										state: { id: get(eachAbschlussbericht, 'id'), api: APIS.Abschlussbericht },
									})
								}
							/>
						);
					})}
				</AntragsButtonWrapper>
			);
		} else {
			if (get(antrag, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.ProjektBeendet) {
				return (
					<AntragsButtonWrapper title='Sachbericht:'>
						<AntragButton name='Sachbericht' description='Sachbericht erstellen' onClick={handleCreateAbschlussbericht} variant='text' />
					</AntragsButtonWrapper>
				);
			} else {
				return (
					<AntragsButtonWrapper title='Sachbericht:'>
						<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} variant='text' disabled={true} />
					</AntragsButtonWrapper>
				);
			}
		}
	};
	const skizzeExtern = get(foerderProgramm, FP_P.Konfiguration_Antragsbearbeitung.path);

	return (
		<>
			<MuiSnackbar error={true} showSnackbar={wsError} closeSnackbar={() => setWsError(false)} alertMessage='Etwas ist schief gelaufen' />
			<LoadingBackdrop open={loading} />
			{/* Antrag und Skizze */}
			{skizzeExtern === SKIZZE_UND_ANTRAG_EXTERN.SkizzeExtern ?
				<AntragsButtonWrapper title='Antrag:'>
					<AntragButton
						name={get(application, P.Allgemein_Projekttitel.path) ? get(application, P.Allgemein_Projekttitel.path) : ANTRAG_BUTTON_NAME}
						description={enumT(get(application, P.Antragstatus_Antragstatus.path))}
						onClick={() =>
							navigate(PAGES_URLS.OnlineAntrag, {
								state: {
									id: get(application, 'id'),
									api: APIS.Antrag,
									antragsformular: get(application, P.Allgemein_Antragsformular.path),
								},
							})
						}
					/>
				</AntragsButtonWrapper>
			:	<Stack direction={'row'} justifyContent={'space-between'}>
					<AntragsButtonWrapper title='Skizze:'>
						<AntragButton
							name={get(application, P.Allgemein_Projekttitel.path) ? get(application, P.Allgemein_Projekttitel.path) : ANTRAG_BUTTON_NAME}
							description={enumT(get(application, P.Antragstatus_Antragstatus.path))}
							onClick={() =>
								navigate(PAGES_URLS.OnlineAntrag, {
									state: {
										id: get(application, 'id'),
										api: APIS.Antrag,
										antragsformular: get(application, P.Allgemein_Antragsformular.path),
									},
								})
							}
						/>
					</AntragsButtonWrapper>

					{get(application, `${P.Skizze_Antrags.path}.id`) ?
						<>
							<AntragsButtonWrapper title='Antrag:'>
								<AntragButton
									name={
										get(application, P.Skizze_Antrags.schema.attributes.Allgemein_Projekttitel.path) ?
											get(application, P.Skizze_Antrags.schema.attributes.Allgemein_Projekttitel.path)
										:	ANTRAG_BUTTON_NAME
									}
									description={enumT(get(application, P.Skizze_Antrags.schema.attributes.Antragstatus_Antragstatus.path))}
									onClick={() =>
										navigate(`${PAGES_URLS.OnlineAntrag}`, {
											state: {
												id: get(application, `${P.Skizze_Antrags.path}.id`),
												api: APIS.Antrag,
												antragsformular: get(application, P.Allgemein_Antragsformular.path),
											},
										})
									}
								/>
							</AntragsButtonWrapper>
						</>
					:	<>
							{get(application, P.Antragstatus_Antragstatus.path) === 'EmpfehlungZurAntragstellung' ?
								<AntragsButtonWrapper title='Antrag:'>
									<AntragButton name={'Antrag erstellen'} description={''} onClick={handleCreateFoerderAntrag} variant={'contained'} />
								</AntragsButtonWrapper>
							:	<AntragsButtonWrapper title='Antrag:'>
									<AntragButton name={'Noch nicht möglich'} description={''} onClick={() => {}} disabled={true} />
								</AntragsButtonWrapper>
							}
						</>
					}
				</Stack>
			}

			{/* Mittelabruf und Verwendungsnachweis */}
			<Stack direction='row' spacing={2} justifyContent={'space-between'}>
				<span>
					<RenderMittelAbrufButtons />
				</span>
				<span>
					<RenderVerwendungsnachweisButtons />
				</span>
			</Stack>

			{/* <RenderZwischenBerichtButtons /> */}
			<RenderAbschlussberichtButton />
		</>
	);
};

export default StartupStipendiumFoerderDashboard;
